import React from "react";
import { Button, GridItem, useDisclosure } from "@chakra-ui/react";
import { IoAdd } from "react-icons/io5";
import { DatePicker, Form, Grid, Modal, TextInput } from "components";
import { useCurrentCase, useToast } from "duck";
import { FormValues, getValidation, useCreateProcedure } from "./duck";

interface CreateProps {
  patientId: number;
}

const Create: React.FC<CreateProps> = ({ patientId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { caseCreationDate, patientProcedures } = useCurrentCase(
    medicalCase => ({
      caseCreationDate: medicalCase.data.createdAt,
      patientProcedures: medicalCase.data.client.patient.patientProcedures
    })
  );

  const procedureStartTime =
    patientProcedures.find(procedure => procedure.name === "Anesthesia")
      ?.startTime || caseCreationDate;

  const toast = useToast({
    status: "success",
    description: "Procedure is added successfully."
  });

  const onSubmit = useCreateProcedure(patientId);

  return (
    <>
      <Button leftIcon={<IoAdd />} onClick={onOpen}>
        Add Procedure
      </Button>
      <Modal title="Procedures" onClose={onClose} isOpen={isOpen} size="lg">
        <Form<FormValues>
          {...getValidation()}
          onSubmit={values =>
            onSubmit(values).then(() => {
              onClose();
              toast();
            })
          }
        >
          {({ setValue, trigger }) => (
            <>
              <Grid py={6} templateColumns="repeat(2, 1fr)">
                <GridItem colSpan={2}>
                  <TextInput name="name" placeholder="Name" label="Name" />
                </GridItem>
                <GridItem>
                  <DatePicker
                    name="startTime"
                    label="Start Time"
                    placeholderText="Start Time"
                    showTimeSelect
                    showTimeSelectOnly
                    dateFormat="h:mm aaaa"
                    onChange={value => {
                      setValue("startTime", value);
                      trigger("endTime");
                    }}
                    filterTime={time =>
                      new Date(time).getTime() >
                      new Date(procedureStartTime).getTime()
                    }
                  />
                </GridItem>
                <GridItem>
                  <DatePicker
                    name="endTime"
                    label="End Time"
                    placeholderText="End Time"
                    showTimeSelect
                    showTimeSelectOnly
                    dateFormat="h:mm aaaa"
                    onChange={value => {
                      setValue("endTime", value);
                      trigger("startTime");
                    }}
                  />
                </GridItem>
              </Grid>
              <Form.Controls submitText="Save">
                <Button
                  color="gray.700"
                  variant="outline"
                  colorScheme="gray"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </Form.Controls>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default Create;
